import { generateMessageId } from './generateId';
import { getTenantLocalTime, getTimezoneForPhone } from './timezoneGuardrail';

export const DEFAULT_SMS_QUIET_HOURS_START = '09:00';
export const DEFAULT_SMS_QUIET_HOURS_END = '20:00';
export const DEFAULT_AUTOPILOT_SEND_HOUR = 9;

const OPT_OUT_PATTERNS = [
  /^\s*(stop|stopall|unsubscribe|cancel|end|quit|revoke|optout|opt out|alto)\s*$/i,
];

const START_PATTERNS = [
  /^\s*(start|yes|unstop)\s*$/i,
];

export function normalizePhoneE164(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  if (digits.length >= 11 && phone?.trim().startsWith('+')) {
    return `+${digits}`;
  }

  return '';
}

export function hasOptedOutFromSms(tenant) {
  if (!tenant) return false;
  if (tenant.contactPreferences?.smsOptOut) return true;

  const logs = tenant.outreachLog || [];
  let optedOut = false;

  for (const entry of logs) {
    const body = entry?.responseText || entry?.inboundBody || '';
    if (START_PATTERNS.some((pattern) => pattern.test(body))) {
      optedOut = false;
      continue;
    }
    if (
      entry?.status === 'blocked_opt_out' ||
      OPT_OUT_PATTERNS.some((pattern) => pattern.test(body))
    ) {
      optedOut = true;
    }
  }

  return optedOut;
}

function parseTimeParts(value, fallback) {
  const source = value || fallback;
  const [rawHour, rawMinute] = String(source).split(':');
  const hour = Number.parseInt(rawHour, 10);
  const minute = Number.parseInt(rawMinute, 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    if (source === fallback) return { hour: 9, minute: 0 };
    return parseTimeParts(fallback, fallback);
  }

  return { hour, minute };
}

function toMinutes(parts) {
  return (parts.hour * 60) + parts.minute;
}

export function getQuietHoursStatus(
  phone,
  quietHoursStart = DEFAULT_SMS_QUIET_HOURS_START,
  quietHoursEnd = DEFAULT_SMS_QUIET_HOURS_END,
) {
  const timezone = getTimezoneForPhone(phone);
  const localDate = getTenantLocalTime(phone);
  const localMinutes = (localDate.getHours() * 60) + localDate.getMinutes();
  const start = parseTimeParts(quietHoursStart, DEFAULT_SMS_QUIET_HOURS_START);
  const end = parseTimeParts(quietHoursEnd, DEFAULT_SMS_QUIET_HOURS_END);
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);
  const safe = localMinutes >= startMinutes && localMinutes <= endMinutes;
  const localTime = localDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  let reason = '';
  if (!safe) {
    if (localMinutes < startMinutes) {
      reason = `Too early to text. It is ${localTime} for this tenant.`;
    } else {
      reason = `Too late to text. It is ${localTime} for this tenant.`;
    }
  }

  return {
    safe,
    localTime,
    timezone,
    reason,
  };
}

export function getSmsBlockReason(
  tenant,
  quietHoursStart = DEFAULT_SMS_QUIET_HOURS_START,
  quietHoursEnd = DEFAULT_SMS_QUIET_HOURS_END,
) {
  if (!tenant?.phone) {
    return 'No phone number is on file for this tenant.';
  }

  if (hasOptedOutFromSms(tenant)) {
    return 'This tenant has opted out of SMS and must text START before messaging resumes.';
  }

  const quietHours = getQuietHoursStatus(tenant.phone, quietHoursStart, quietHoursEnd);
  if (!quietHours.safe) {
    return `${quietHours.reason} SMS is limited to ${quietHoursStart}–${quietHoursEnd} local time by default.`;
  }

  return '';
}

export function ensureSmsComplianceBody(message, lang = 'en') {
  const trimmed = String(message || '').trim();
  if (!trimmed) return '';

  const footer = lang === 'es'
    ? 'Responda ALTO para dejar de recibir mensajes.'
    : 'Reply STOP to unsubscribe.';

  const hasFooter = /reply\s+stop|responda\s+alto/i.test(trimmed);
  return hasFooter ? trimmed : `${trimmed} ${footer}`;
}

export function createPortalAccessToken() {
  return generateMessageId().replace(/-/g, '');
}

export function buildSecurePaymentLink({
  origin,
  propertyId,
  tenantId,
  messageId,
  channel = 'sms',
  token,
}) {
  const url = new URL(`/pay/${propertyId}/${tenantId}`, origin);
  if (messageId) url.searchParams.set('m', messageId);
  if (channel) url.searchParams.set('c', channel);
  if (token) url.searchParams.set('t', token);
  return url.toString();
}
